import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Camera, CameraOff, Check, Loader2, Minus, Plus, Printer, Search, ShoppingBag, TriangleAlert, WifiOff, X } from "lucide-react";

/* L'entrée d'une représentation (école, 30.10.2026) : la page des bénévoles de l'entrée,
   ouverte depuis le lien que l'école leur envoie (/entree?r=…&cle=…, un lien par
   représentation). Scanner les billets avec la caméra du téléphone, chercher un nom,
   vendre des billets sur place (cash ou TWINT). Rien d'autre : pas de compte, pas de
   données des autres représentations.

   À toute épreuve (Jen, 01.11.2026 : « sur place, s'il y a un bug, on ne peut pas faire
   grand-chose ») :
   · le numéro imprimé se tape comme on veut (HV-SMP9P, hv smp9p, SMP9P) ;
   · chaque refus dit pourquoi, en couleur (vert : entrer ; orange : déjà entré ; rouge : pas
     aujourd'hui, annulé, pas payé, introuvable), avec un bip et une vibration ;
   · sans réseau, on continue : la liste des billets est gardée sur le téléphone, les entrées
     et les ventes aussi, et partent toutes seules quand le réseau revient (jamais deux fois) ;
   · plusieurs téléphones en même temps sur le même lien : le compte reste juste ;
   · une erreur de comptage se corrige (« retirer une entrée ») ;
   · une liste papier de secours s'imprime depuis « Chercher ». */

const B = process.env.REACT_APP_BACKEND_URL;
const DELAI = 8000;
const chf = (n) => `CHF ${Number(n || 0).toFixed(2)}`;
const LIBELLES = { plein: "Plein", enfant: "3-12 ans", bebe: "0-2 ans" };
const majuscule = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : "");
const pluriel = (n, mot) => `${n} ${mot}${Math.abs(n) > 1 ? "s" : ""}`;

class Reseau extends Error {}

/* Un appel au serveur. Pas de réponse en 8 s, pas de réseau, ou le serveur qui redémarre
   (5xx) : « Reseau » — la page continue avec ce qu'elle a gardé. */
async function appel(chemin, options = {}) {
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const minuterie = ctl ? setTimeout(() => ctl.abort(), DELAI) : null;
  let r;
  try {
    r = await fetch(chemin, ctl ? { ...options, signal: ctl.signal } : options);
  } catch (e) {
    throw new Reseau("Pas de réseau");
  } finally {
    if (minuterie) clearTimeout(minuterie);
  }
  if (r.status >= 500) throw new Reseau(`Serveur indisponible (${r.status})`);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(d.detail || `Erreur ${r.status}`);
    e.status = r.status;
    throw e;
  }
  return d;
}
const post = (corps) => ({ method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(corps) });

const lire = (cle, defaut) => { try { const v = window.localStorage.getItem(cle); return v ? JSON.parse(v) : defaut; } catch (e) { return defaut; } };
const ecrire = (cle, v) => { try { window.localStorage.setItem(cle, JSON.stringify(v)); } catch (e) { /* stockage plein ou bloqué */ } };
const nouvelOp = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/* Comme le serveur : le jeton d'un QR (…/billet/<jeton>), ou le numéro imprimé tapé. */
const jetonDe = (code) => { const m = /\/billet\/([A-Za-z0-9_-]+)/.exec(code || ""); return m ? m[1] : String(code || "").trim(); };
export const numeroDe = (code) => {
  const t = String(code || "").replace(/[\s\-_.·]/g, "").toUpperCase();
  if (/^[A-Z]{2}[A-Z0-9]{5}$/.test(t)) return `${t.slice(0, 2)}-${t.slice(2)}`;
  if (/^[A-Z0-9]{5}$/.test(t)) return `*-${t}`;
  return "";
};
const trouverLocal = (liste, code) => {
  const j = jetonDe(code);
  const b = liste.find((x) => x.jeton === j);
  if (b) return b;
  const n = numeroDe(code);
  if (!n) return null;
  return liste.find((x) => (n.startsWith("*-") ? x.numero.endsWith(n.slice(1)) : x.numero === n)) || null;
};

/* Un bip (l'iPhone ne vibre pas depuis une page web) et une vibration (Android). Le son
   s'active au premier toucher (« Ouvrir la caméra », « OK »). */
let audio = null;
const activerSon = () => {
  try {
    if (!audio) audio = new (window.AudioContext || window.webkitAudioContext)();
    if (audio.state === "suspended") audio.resume();
  } catch (e) { audio = null; }
};
const signal = (ton) => {
  try { if (navigator.vibrate) navigator.vibrate(ton === "ok" ? 80 : [180, 80, 180]); } catch (e) { /* rien */ }
  if (!audio) return;
  try {
    const notes = ton === "ok" ? [[880, 0, 0.12]] : ton === "attention" ? [[520, 0, 0.15], [520, 0.2, 0.15]] : [[240, 0, 0.2], [200, 0.25, 0.3]];
    notes.forEach(([f, debut, duree]) => {
      const o = audio.createOscillator();
      const g = audio.createGain();
      o.frequency.value = f;
      g.gain.value = 0.15;
      o.connect(g); g.connect(audio.destination);
      o.start(audio.currentTime + debut);
      o.stop(audio.currentTime + debut + duree);
    });
  } catch (e) { /* rien */ }
};

const Pas = ({ label, valeur, onChange, max = 50 }) => (
  <div className="flex items-center justify-between py-2">
    <span>{label}</span>
    <span className="flex items-center gap-3">
      <button type="button" aria-label="moins" disabled={valeur <= 0} className="w-11 h-11 rounded-full border grid place-items-center disabled:opacity-30" onClick={() => onChange(Math.max(0, valeur - 1))}><Minus size={16} /></button>
      <span className="w-6 text-center text-lg font-semibold tabular-nums">{valeur}</span>
      <button type="button" aria-label="plus" disabled={valeur >= max} className="w-11 h-11 rounded-full border grid place-items-center disabled:opacity-30" onClick={() => onChange(Math.min(max, valeur + 1))}><Plus size={16} /></button>
    </span>
  </div>
);

const TONS = { ok: "#14532D", erreur: "#7F1D1D", info: "#1E3A5F", attention: "#78350F" };

export default function Entree() {
  const [params] = useSearchParams();
  const rep = params.get("r") || "";
  const cle = params.get("cle") || "";
  const base = `${B}/api/billetterie/entree/${encodeURIComponent(rep)}`;
  const cleListe = `entree-liste-${rep}`;
  const cleFile = `entree-file-${rep}`;
  const cleInfo = `entree-info-${rep}`;

  const [info, setInfo] = useState(() => lire(cleInfo, null));
  const [fatal, setFatal] = useState("");
  const [liste, setListe] = useState(() => lire(cleListe, { billets: [] }).billets || []);
  const [file, setFile] = useState(() => lire(cleFile, []));
  const [enLigne, setEnLigne] = useState(true);
  const [problemes, setProblemes] = useState([]);
  const [vue, setVue] = useState("scanner");
  const [billet, setBillet] = useState(null);
  const [nombre, setNombre] = useState(0);
  const [message, setMessage] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  // La caméra reste allumée d'un billet à l'autre (en pause pendant qu'un billet est affiché).
  const [camera, setCamera] = useState(false);

  const fileRef = useRef(file);
  const listeRef = useRef(liste);
  const infoRef = useRef(info);
  const vidage = useRef(false);
  listeRef.current = liste;
  infoRef.current = info;

  const majFile = useCallback((f) => { fileRef.current = f; setFile(f); ecrire(cleFile, f); }, [cleFile]);
  const majBillet = useCallback((b) => {
    if (!b || !b.jeton) return;
    setListe((l) => {
      const n = l.some((x) => x.jeton === b.jeton) ? l.map((x) => (x.jeton === b.jeton ? { ...x, entres: b.entres, restent: b.restent, entre_a: b.entre_a } : x)) : l;
      ecrire(cleListe, { billets: n });
      return n;
    });
  }, [cleListe]);
  const dire = (ton, texte) => setMessage({ ton, texte });

  // Envoie ce qui a été gardé sur le téléphone pendant une coupure, dans l'ordre.
  const vider = useCallback(async () => {
    if (vidage.current || !fileRef.current.length) return;
    vidage.current = true;
    try {
      while (fileRef.current.length) {
        const o = fileRef.current[0];
        try {
          if (o.type === "entrer") majBillet(await appel(`${base}/entrer`, post({ cle, jeton: o.jeton, nombre: o.nombre, op: o.op })));
          else await appel(`${base}/vente`, post({ cle, ...o.vente, op: o.op, hors_ligne: true }));
        } catch (e) {
          if (e instanceof Reseau) { setEnLigne(false); return; }
          setProblemes((p) => [...p, `${o.numero || "Vente sur place"} : ${e.message}`]);
        }
        majFile(fileRef.current.slice(1));
      }
      setEnLigne(true);
    } finally {
      vidage.current = false;
    }
  }, [base, cle, majBillet, majFile]);

  const charger = useCallback(async () => {
    await vider();
    try {
      const [i, l] = await Promise.all([appel(`${base}?cle=${encodeURIComponent(cle)}`), appel(`${base}/liste?cle=${encodeURIComponent(cle)}`)]);
      setInfo(i); ecrire(cleInfo, i);
      setListe(l.billets || []); ecrire(cleListe, { billets: l.billets || [] });
      setEnLigne(true); setFatal("");
    } catch (e) {
      if (e instanceof Reseau) {
        setEnLigne(false);
        if (!infoRef.current) setFatal("Pas de réseau. Vérifie le wifi ou les données mobiles, puis réessaie.");
      } else if (!infoRef.current || e.status === 403 || e.status === 404) {
        setFatal(e.message);
      }
    }
  }, [base, cle, cleInfo, cleListe, vider]);

  useEffect(() => {
    charger();
    const t = setInterval(charger, 20000);
    const revenu = () => charger();
    const parti = () => setEnLigne(false);
    window.addEventListener("online", revenu);
    window.addEventListener("offline", parti);
    return () => { clearInterval(t); window.removeEventListener("online", revenu); window.removeEventListener("offline", parti); };
  }, [charger]);

  // L'écran reste allumé pendant le scan (si le téléphone le permet).
  useEffect(() => {
    if (!camera || !navigator.wakeLock) return undefined;
    let verrou = null;
    navigator.wakeLock.request("screen").then((v) => { verrou = v; }).catch(() => {});
    return () => { if (verrou) verrou.release().catch(() => {}); };
  }, [camera]);

  // Les entrées gardées sur le téléphone comptent déjà, à l'écran.
  const gardees = useCallback((jeton) => file.filter((o) => o.type === "entrer" && o.jeton === jeton)
    .reduce((s, o) => s + o.nombre, 0), [file]);
  const vueLocale = (b) => {
    const entres = Math.max(0, Math.min(b.total, (b.entres || 0) + gardees(b.jeton)));
    return { ...b, entres, restent: b.total - entres, autre_representation: false, hors_ligne: !enLigne };
  };
  const compteurs = useMemo(() => {
    if (!info) return null;
    let entres = info.entres;
    let vendues = info.vendues;
    let attendus = info.attendus;
    file.forEach((o) => {
      if (o.type === "entrer") { entres += o.nombre; return; }
      const n = o.vente.plein + o.vente.enfant;
      vendues += n; entres += n + o.vente.bebe; attendus += n + o.vente.bebe;
    });
    return { entres: Math.max(0, entres), attendus, vendues, restantes: Math.max(0, info.places - vendues), places: info.places };
  }, [info, file]);

  const montrer = (b) => { setBillet(b); setNombre(b.autre_representation ? 0 : b.restent); };
  const scanne = async (code) => {
    if (!String(code || "").trim()) return;
    setMessage(null);
    try {
      const b = await appel(`${base}/scan`, post({ cle, code }));
      setEnLigne(true);
      montrer(b);
      signal(b.autre_representation ? "erreur" : b.restent ? "ok" : "attention");
    } catch (e) {
      if (e instanceof Reseau) {
        setEnLigne(false);
        const b = trouverLocal(listeRef.current, code);
        if (b) {
          const entres = Math.max(0, Math.min(b.total, (b.entres || 0) + gardees(b.jeton)));
          montrer({ ...b, entres, restent: b.total - entres, autre_representation: false, hors_ligne: true });
          signal(entres < b.total ? "ok" : "attention");
          return;
        }
        setBillet(null);
        dire("erreur", "Pas de réseau, et ce billet n'est pas dans la liste de ce spectacle gardée sur le téléphone. Cherche le nom dans « Chercher », ou note le numéro et laisse entrer si tu as un doute.");
      } else {
        setBillet(null);
        dire("erreur", e.message);
      }
      signal("erreur");
    }
  };

  const entrer = async (n) => {
    const b = billet;
    const op = nouvelOp();
    setEnvoi(true);
    try {
      const r = await appel(`${base}/entrer`, post({ cle, jeton: b.jeton, nombre: n, op }));
      majBillet(r);
      setEnLigne(true);
      if (n < 0) dire("info", `Corrigé : ${r.entres} entrée${r.entres > 1 ? "s" : ""} sur ${r.total} · ${r.numero}`);
      else if (r.admis === n) dire("ok", `✓ ${pluriel(n, "personne")} entrée${n > 1 ? "s" : ""} · ${r.numero}`);
      else if (r.admis > 0) dire("attention", `✓ ${r.admis} entrée${r.admis > 1 ? "s" : ""} seulement : les autres venaient d'entrer avec un autre téléphone · ${r.numero}`);
      else dire("attention", `Rien de plus : tout le monde était déjà entré · ${r.numero}`);
      setBillet(null);
      charger();
    } catch (e) {
      if (e instanceof Reseau) {
        majFile([...fileRef.current, { type: "entrer", op, jeton: b.jeton, nombre: n, numero: b.numero }]);
        setEnLigne(false);
        dire("ok", `✓ ${n < 0 ? "Corrigé" : `${pluriel(n, "personne")} entrée${n > 1 ? "s" : ""}`} · ${b.numero} — sans réseau : gardé sur ce téléphone, envoyé dès que le réseau revient.`);
        setBillet(null);
      } else {
        dire("erreur", e.message);
        signal("erreur");
      }
    } finally {
      setEnvoi(false);
    }
  };

  const vendre = async (nb, mode) => {
    const op = nouvelOp();
    const montant = nb.plein * info.tarifs.plein + nb.enfant * info.tarifs.enfant;
    const total = nb.plein + nb.enfant + nb.bebe;
    try {
      const d = await appel(`${base}/vente`, post({ cle, ...nb, mode, op }));
      setEnLigne(true);
      dire("ok", `✓ Vendu (${mode === "cash" ? "cash" : "TWINT"}) : ${chf(d.commande.montant)} · ${pluriel(d.commande.total, "personne")} entrée${d.commande.total > 1 ? "s" : ""} · ${d.commande.numero}`);
      charger();
      return true;
    } catch (e) {
      if (e instanceof Reseau) {
        majFile([...fileRef.current, { type: "vente", op, vente: { ...nb, mode }, numero: "Vente sur place" }]);
        setEnLigne(false);
        dire("ok", `✓ Vendu (${mode === "cash" ? "cash" : "TWINT"}) : ${chf(montant)} · ${pluriel(total, "personne")} — sans réseau : gardé sur ce téléphone, envoyé dès que le réseau revient.`);
        return true;
      }
      throw e;
    }
  };

  if (fatal && !info) {
    return (
      <Plein>
        <TriangleAlert className="mx-auto mb-3" />
        <p className="text-center" data-testid="entree-fatal">{fatal}</p>
        <button className="mt-4 mx-auto block rounded-full px-5 py-3 bg-white/15" onClick={() => { setFatal(""); charger(); }}>Réessayer</button>
      </Plein>
    );
  }
  if (fatal) return <Plein><TriangleAlert className="mx-auto mb-3" /><p className="text-center" data-testid="entree-fatal">{fatal}</p></Plein>;
  if (!info) return <Plein><Loader2 className="animate-spin mx-auto" /></Plein>;
  return (
    <div className="min-h-screen bg-[#0F1B2D] text-white" data-testid="page-entree">
      <style>{"@media print { .entree-ecran { display: none !important } .entree-papier { display: block !important } body { background: #fff } }"}</style>
      <div className="entree-ecran max-w-lg mx-auto p-4 pt-8 space-y-4">
        <header>
          <div className="text-xs uppercase tracking-[0.16em] text-[#66EAF0] font-semibold">Entrée · {info.titre}</div>
          <div className="text-lg font-semibold">{majuscule(info.quand)}</div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-center" data-testid="entree-compteurs">
            {[["Entrés", `${compteurs.entres}/${compteurs.attendus}`], ["Places vendues", `${compteurs.vendues}/${compteurs.places}`], ["Places libres", compteurs.restantes]].map(([l, v]) => (
              <div key={l} className="rounded-2xl bg-white/10 py-2"><div className="text-xl font-bold tabular-nums">{v}</div><div className="text-[11px] text-white/70">{l}</div></div>
            ))}
          </div>
        </header>

        {(!enLigne || file.length > 0) && (
          <div className="rounded-2xl px-4 py-3 text-sm flex gap-2 items-start" style={{ background: enLigne ? "#1E3A5F" : "#78350F" }} data-testid="entree-reseau">
            {enLigne ? <Loader2 size={16} className="animate-spin mt-0.5 shrink-0" /> : <WifiOff size={16} className="mt-0.5 shrink-0" />}
            <span>{enLigne ? `Envoi de ${pluriel(file.length, "geste")} gardé${file.length > 1 ? "s" : ""} sur ce téléphone…`
              : <>Sans réseau : continue normalement, les billets sont vérifiés avec la liste gardée sur le téléphone.{file.length ? ` ${pluriel(file.length, "geste")} gardé${file.length > 1 ? "s" : ""}, envoyé${file.length > 1 ? "s" : ""} dès que le réseau revient.` : ""}</>}</span>
          </div>
        )}
        {problemes.length > 0 && (
          <div className="rounded-2xl px-4 py-3 text-sm space-y-1" style={{ background: "#7F1D1D" }} data-testid="entree-problemes">
            <div className="flex items-center"><strong className="flex-1">Pas enregistré au retour du réseau :</strong>
              <button aria-label="Fermer" onClick={() => setProblemes([])}><X size={16} /></button></div>
            {problemes.map((p, k) => <div key={k}>{p}</div>)}
          </div>
        )}

        <nav className="grid grid-cols-3 gap-1 rounded-full bg-white/10 p-1 text-sm">
          {[["scanner", "Scanner", Camera], ["chercher", "Chercher", Search], ["vendre", "Vendre", ShoppingBag]].map(([id, l, Icon]) => (
            <button key={id} onClick={() => { setVue(id); setBillet(null); setMessage(null); }} data-testid={`entree-onglet-${id}`}
              className="rounded-full py-2 inline-flex items-center justify-center gap-1.5" style={vue === id ? { background: "#00C6CE", color: "#0F1B2D", fontWeight: 600 } : undefined}>
              <Icon size={15} /> {l}
            </button>
          ))}
        </nav>
        {message && <div className="rounded-2xl px-4 py-3 text-[15px]" style={{ background: TONS[message.ton] }} data-testid="entree-message" data-ton={message.ton}>{message.texte}</div>}

        {billet && <CarteBillet b={billet} nombre={nombre} setNombre={setNombre} envoi={envoi}
          onEntrer={() => entrer(nombre)} onCorriger={() => entrer(-1)} onRetour={() => { setBillet(null); setMessage(null); }} />}
        {/* Le scanner reste monté (la caméra ne se rallume pas à chaque billet) ; en pause pendant qu'un billet est affiché. */}
        {vue === "scanner" && <div style={billet ? { display: "none" } : undefined}><Scanner onCode={scanne} actif={camera} setActif={setCamera} pause={!!billet} /></div>}
        {!billet && vue === "chercher" && <Chercher liste={liste} gardees={gardees} info={info} onChoix={(b) => { activerSon(); montrer(vueLocale(b)); }} />}
        {!billet && vue === "vendre" && <Vendre info={info} restantes={compteurs.restantes} onVendre={vendre} />}
      </div>
      <ListePapier info={info} liste={liste} gardees={gardees} />
    </div>
  );
}

const Plein = ({ children }) => <div className="min-h-screen grid place-items-center bg-[#0F1B2D] text-white p-6"><div>{children}</div></div>;

/* Le billet scanné : vert (entrer), orange (déjà tous entrés), rouge (pas aujourd'hui). */
function CarteBillet({ b, nombre, setNombre, envoi, onEntrer, onCorriger, onRetour }) {
  const etat = b.autre_representation ? "rouge" : b.restent ? "vert" : "orange";
  const bandeau = {
    vert: ["#16A34A", "✓ Billet valable"],
    orange: ["#D97706", `Déjà ${b.total > 1 ? "tous entrés" : "entré"}${b.entre_a ? ` (dernière entrée à ${b.entre_a})` : ""}`],
    rouge: ["#DC2626", "✕ Pas pour aujourd'hui"],
  }[etat];
  const mode = String(b.mode || "");
  return (
    <div className="rounded-3xl bg-white text-[#0F1B2D] overflow-hidden" data-testid="entree-billet" data-etat={etat}>
      <div className="px-5 py-3 text-white text-lg font-bold" style={{ background: bandeau[0] }} data-testid="entree-bandeau">{bandeau[1]}</div>
      <div className="p-5 space-y-3">
        {b.autre_representation && (
          <div className="rounded-2xl px-3 py-2 text-sm font-semibold" style={{ background: "#FEF2F2", color: "#B91C1C" }}>
            Ce billet est pour {b.titre ? `${b.titre}, ` : ""}{b.quand}. La personne peut acheter un billet pour aujourd'hui (« Vendre »).
          </div>
        )}
        {b.remplace && (
          <div className="rounded-2xl px-3 py-2 text-sm" style={{ background: "#EFF6FF", color: "#1E40AF" }}>
            Le QR montré est celui du {b.remplace_quand} ({b.remplace}) : voici leur billet d'aujourd'hui.
          </div>
        )}
        {etat === "orange" && (
          <div className="rounded-2xl px-3 py-2 text-sm" style={{ background: "#FFFBEB", color: "#92400E" }}>
            Ce billet a déjà servi. Si la personne n'est pas encore entrée, demande son nom et vérifie (capture d'écran partagée ?).
          </div>
        )}
        {b.hors_ligne && <div className="text-xs text-slate-500 flex items-center gap-1"><WifiOff size={12} /> Sans réseau : vérifié avec la liste gardée sur le téléphone.</div>}
        <div className="text-xs text-slate-500">{b.numero} · {mode === "benevole" ? "billet offert (bénévole)" : mode.startsWith("sur_place") ? "vendu sur place" : "acheté en ligne"}</div>
        <div className="text-2xl font-bold">{`${b.prenom || ""} ${b.nom || ""}`.trim() || "—"}</div>
        <div className="text-sm">{["plein", "enfant", "bebe"].filter((k) => b[k] > 0).map((k) => `${b[k]} × ${LIBELLES[k]}`).join(" · ")}</div>
        {!b.autre_representation && <div className="text-sm">{b.restent ? `Encore ${b.restent} à faire entrer sur ${b.total}` : `${b.total} sur ${b.total} entrés.`}</div>}
        {!b.autre_representation && b.restent > 0 && (
          <>
            <Pas label="Faire entrer" valeur={nombre} onChange={setNombre} max={b.restent} />
            <button className="w-full rounded-full py-4 text-lg font-bold text-white disabled:opacity-40" style={{ background: "#16A34A" }}
              disabled={!nombre || envoi} onClick={onEntrer} data-testid="entree-entrer">
              {envoi ? <Loader2 className="inline animate-spin" /> : <><Check className="inline -mt-1" /> Entrer {nombre}</>}</button>
          </>
        )}
        <button className="w-full rounded-full py-3 border font-semibold" onClick={onRetour} data-testid="entree-retour">Scanner le suivant</button>
        {!b.autre_representation && b.entres > 0 && (
          <button className="w-full text-sm text-slate-500 underline py-1" disabled={envoi} onClick={onCorriger} data-testid="entree-corriger">
            Erreur de comptage ? Retirer une entrée ({b.entres} entrée{b.entres > 1 ? "s" : ""})</button>
        )}
      </div>
    </div>
  );
}

/* La caméra arrière, décodée ~8 fois par seconde sur une image réduite (jsQR, chargé
   seulement ici) : rapide même sur un vieux téléphone. Un QR resté devant la caméra n'est
   pas relu : il doit disparaître 2,5 s avant de compter à nouveau. Sans caméra : le numéro. */
function Scanner({ onCode, actif, setActif, pause }) {
  const video = useRef(null);
  const [erreur, setErreur] = useState("");
  const [manuel, setManuel] = useState("");
  const vu = useRef({ code: "", t: 0 });
  const pauseRef = useRef(pause);
  const rappel = useRef(onCode);
  rappel.current = onCode;
  useEffect(() => {
    pauseRef.current = pause;
    if (!pause) vu.current = { ...vu.current, t: Date.now() };
  }, [pause]);

  useEffect(() => {
    if (!actif) return undefined;
    let flux = null;
    let boucle = null;
    let fini = false;
    (async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw Object.assign(new Error("pas de caméra"), { name: "NonSupporte" });
        }
        const { default: jsQR } = await import("jsqr");
        flux = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        if (fini) { flux.getTracks().forEach((t) => t.stop()); return; }
        video.current.srcObject = flux;
        await video.current.play().catch(() => {});
        const toile = document.createElement("canvas");
        const ctx = toile.getContext("2d", { willReadFrequently: true });
        const lireImage = () => {
          if (fini) return;
          const v = video.current;
          if (!pauseRef.current && v && v.readyState >= 2 && v.videoWidth) {
            const echelle = Math.min(1, 720 / Math.max(v.videoWidth, v.videoHeight));
            const w = Math.round(v.videoWidth * echelle);
            const h = Math.round(v.videoHeight * echelle);
            if (toile.width !== w || toile.height !== h) { toile.width = w; toile.height = h; }
            ctx.drawImage(v, 0, 0, w, h);
            const code = jsQR(ctx.getImageData(0, 0, w, h).data, w, h, { inversionAttempts: "dontInvert" });
            if (code && code.data) {
              const ici = Date.now();
              const meme = code.data === vu.current.code && ici - vu.current.t < 2500;
              vu.current = { code: code.data, t: ici };
              if (!meme) rappel.current(code.data);
            }
          }
          boucle = setTimeout(lireImage, 120);
        };
        lireImage();
      } catch (e) {
        const n = e && e.name;
        setErreur(n === "NotAllowedError" || n === "SecurityError"
          ? "Caméra refusée. iPhone : touche « aA » dans la barre d'adresse › Réglages du site web › Caméra › Autoriser (ou Réglages › Safari › Caméra), puis recharge la page. Android : touche le cadenas à côté de l'adresse › Autorisations › Caméra. En attendant, tape le numéro du billet ci-dessous."
          : n === "NotFoundError" || n === "OverconstrainedError" ? "Pas de caméra trouvée sur cet appareil : tape le numéro du billet ci-dessous."
            : n === "NotReadableError" ? "La caméra est utilisée par une autre app : ferme-la et réessaie, ou tape le numéro ci-dessous."
              : "Caméra indisponible ici. Ouvre le lien dans Safari ou Chrome (pas dans WhatsApp ou Instagram), ou tape le numéro du billet ci-dessous.");
        setActif(false);
      }
    })();
    return () => { fini = true; clearTimeout(boucle); if (flux) flux.getTracks().forEach((t) => t.stop()); };
  }, [actif, setActif]);

  return (
    <div className="space-y-3" data-testid="entree-scanner">
      <div className="rounded-3xl overflow-hidden bg-black aspect-square grid place-items-center relative">
        {actif ? (
          <>
            <video ref={video} playsInline muted autoPlay className="w-full h-full object-cover" data-testid="entree-video" />
            <div className="absolute inset-[18%] rounded-3xl border-4 border-white/70 pointer-events-none" />
          </>
        ) : <button className="rounded-full px-6 py-4 font-bold text-[#0F1B2D]" style={{ background: "#00C6CE" }} onClick={() => { setErreur(""); activerSon(); setActif(true); }} data-testid="entree-camera">
          <Camera className="inline -mt-1" /> Ouvrir la caméra</button>}
      </div>
      {actif && <button className="w-full rounded-full py-2 border border-white/30 text-sm" onClick={() => setActif(false)}><CameraOff size={14} className="inline -mt-0.5" /> Arrêter la caméra</button>}
      {erreur && <p className="text-sm text-amber-300" data-testid="entree-erreur-camera">{erreur}</p>}
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); activerSon(); if (manuel.trim()) { onCode(manuel.trim()); setManuel(""); } }}>
        <input className="flex-1 min-w-0 rounded-full px-4 py-3 text-[#0F1B2D] uppercase placeholder:normal-case" placeholder="Numéro du billet, ex. HV-7K2QX"
          value={manuel} onChange={(e) => setManuel(e.target.value)} autoCapitalize="characters" autoCorrect="off" autoComplete="off" spellCheck={false}
          data-testid="entree-manuel" />
        <button className="rounded-full px-5 font-semibold" style={{ background: "#00C6CE", color: "#0F1B2D" }} data-testid="entree-manuel-ok">OK</button>
      </form>
    </div>
  );
}

/* Chercher un nom ou un numéro dans la liste gardée sur le téléphone (marche sans réseau,
   rafraîchie toutes les 20 s). Vide : tous les billets, ceux pas encore entrés d'abord. */
function Chercher({ liste, gardees, info, onChoix }) {
  const [q, setQ] = useState("");
  const t = q.trim().toLowerCase();
  const n = numeroDe(q);
  const entresDe = (b) => Math.max(0, Math.min(b.total, (b.entres || 0) + gardees(b.jeton)));
  const trouves = liste.filter((b) => !t || `${b.prenom || ""} ${b.nom || ""}`.toLowerCase().includes(t) || b.numero.toLowerCase().includes(t)
    || (n && (n.startsWith("*-") ? b.numero.endsWith(n.slice(1)) : b.numero === n)))
    .sort((a, b) => (t ? 0 : (entresDe(a) >= a.total) - (entresDe(b) >= b.total)));
  return (
    <div className="space-y-3" data-testid="entree-chercher">
      <input className="w-full rounded-full px-4 py-3 text-[#0F1B2D]" placeholder="Nom ou numéro (HV-…)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="entree-chercher-champ" />
      <div className="text-xs text-white/60">{t ? `${trouves.length} billet${trouves.length > 1 ? "s" : ""} trouvé${trouves.length > 1 ? "s" : ""}` : `${liste.length} billet${liste.length > 1 ? "s" : ""} pour ${info.quand}`}</div>
      {trouves.slice(0, 60).map((b) => {
        const e = entresDe(b);
        return (
          <button key={b.jeton} className="w-full text-left rounded-2xl bg-white/10 px-4 py-3" onClick={() => onChoix(b)} data-testid="entree-resultat">
            <div className="font-semibold">{`${b.prenom || ""} ${b.nom || ""}`.trim() || "—"}</div>
            <div className="text-xs text-white/70">{b.numero} · {pluriel(b.total, "personne")} · {e >= b.total ? "tous entrés" : e ? `${e} entrée${e > 1 ? "s" : ""}, ${b.total - e} à faire entrer` : "pas encore entré"}</div>
          </button>
        );
      })}
      {trouves.length > 60 && <p className="text-xs text-white/60 text-center">… et {trouves.length - 60} autres : tape un nom pour les trouver.</p>}
      <button className="w-full rounded-full py-3 border border-white/30 text-sm inline-flex items-center justify-center gap-2" onClick={() => window.print()} data-testid="entree-liste-papier">
        <Printer size={15} /> Liste papier de secours (imprimer)</button>
    </div>
  );
}

function Vendre({ info, restantes, onVendre }) {
  const [nb, setNb] = useState({ plein: 0, enfant: 0, bebe: 0 });
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const total = nb.plein * info.tarifs.plein + nb.enfant * info.tarifs.enfant;
  const libres = Math.max(0, (restantes || 0) - nb.plein - nb.enfant);
  const vendre = async (mode) => {
    setErreur(""); setEnvoi(true);
    try {
      if (await onVendre(nb, mode)) setNb({ plein: 0, enfant: 0, bebe: 0 });
    } catch (e) { setErreur(e.message); } finally { setEnvoi(false); }
  };
  return (
    <div className="rounded-3xl bg-white text-[#0F1B2D] p-5 space-y-2" data-testid="entree-vendre">
      {/* Pas de maximum par vente (école, 30.10.2026) : seules les places restantes limitent. */}
      <Pas label={`Plein tarif · ${info.tarifs.plein}.–`} valeur={nb.plein} onChange={(v) => setNb({ ...nb, plein: v, bebe: Math.min(nb.bebe, v) })} max={nb.plein + libres} />
      <Pas label={`3 à 12 ans · ${info.tarifs.enfant}.–`} valeur={nb.enfant} onChange={(v) => setNb({ ...nb, enfant: v })} max={nb.enfant + libres} />
      <Pas label="0 à 2 ans · gratuit" valeur={nb.bebe} onChange={(v) => setNb({ ...nb, bebe: v })} max={nb.plein} />
      {!nb.plein && <p className="text-xs text-slate-500 -mt-1">Un 0-2 ans vient avec un adulte (plein tarif).</p>}
      <div className="text-2xl font-bold text-center py-2">À encaisser : {chf(total)}</div>
      {erreur && <p className="text-sm text-red-700" data-testid="entree-vendre-erreur">{erreur}</p>}
      <div className="grid grid-cols-2 gap-2">
        <button className="rounded-full py-4 font-bold text-white disabled:opacity-40" style={{ background: "#16A34A" }} disabled={envoi || !(nb.plein + nb.enfant)} onClick={() => vendre("cash")} data-testid="entree-vendre-cash">Payé cash</button>
        <button className="rounded-full py-4 font-bold text-white disabled:opacity-40" style={{ background: "#0F1B2D" }} disabled={envoi || !(nb.plein + nb.enfant)} onClick={() => vendre("twint")} data-testid="entree-vendre-twint">Payé TWINT</button>
      </div>
      <p className="text-xs text-slate-500 text-center">Les personnes comptent comme entrées tout de suite.</p>
    </div>
  );
}

/* La liste papier de secours (Chercher › « Liste papier ») : tous les billets de la
   représentation, par nom, avec une case à cocher. Visible seulement à l'impression. */
function ListePapier({ info, liste, gardees }) {
  const heure = new Date().toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  const th = { textAlign: "left", borderBottom: "1px solid #000", padding: "4px 6px", fontSize: 11 };
  const td = { borderBottom: "1px solid #ccc", padding: "4px 6px", fontSize: 12 };
  return (
    <div className="entree-papier" style={{ display: "none", color: "#000", background: "#fff", padding: 16, fontFamily: "Arial, sans-serif" }} data-testid="entree-papier">
      <h1 style={{ fontSize: 18, margin: 0 }}>Entrée · {info.titre} · {majuscule(info.quand)}</h1>
      <p style={{ fontSize: 11, margin: "4px 0 10px" }}>{liste.length} billets · {liste.reduce((s, b) => s + b.total, 0)} personnes · liste du {heure}</p>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>{["Nom", "Numéro", "Plein", "3-12", "0-2", "Total", "Entrés", "✓"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {liste.map((b) => (
            <tr key={b.jeton}>
              <td style={td}>{`${b.nom || ""} ${b.prenom || ""}`.trim() || "—"}</td>
              <td style={td}>{b.numero}</td>
              <td style={td}>{b.plein || ""}</td><td style={td}>{b.enfant || ""}</td><td style={td}>{b.bebe || ""}</td>
              <td style={td}><strong>{b.total}</strong></td>
              <td style={td}>{Math.max(0, (b.entres || 0) + gardees(b.jeton)) || ""}</td>
              <td style={td}>☐</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
